// hooks/useCreateNewReservation.ts
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store"; // Adjust the import based on your file structure
import { stationSlotWindow } from "./reservationWindow";
import { useSelectedLocationTimeZone } from "../../util/locationTimeZone";
import { CreateNewReservationData } from "../../api/types";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { useCreateNewReservationMutation } from "../../api/reservations/reservationsApiSlice";
import { processReservationData } from "./processors";
import { reservationCreated } from "../slice/bookingCreation/bookingCreationSlice";
import { notifyBookingFailure } from "../helpers/failureNotification";
// Note: Previously we normalized times to noon; we now preserve the exact user-selected time.

export const useCreateNewReservation = (useCase?: string, customerAgreementId?: string) => {
  const assetGroupSelection = useSelector(
    (state: RootState) => state.bookingCreation.assetGroupId
  );
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  const selectedServiceType = useSelector(
    (state: RootState) => state.bookingCreation.keyHandoverSelectedService
  );
  const [createNewReservationMutation] = useCreateNewReservationMutation();
  const dispatch = useDispatch();
  // The booked location's clock (#3219) — the server reads the window there.
  const timeZone = useSelectedLocationTimeZone();

  const keyHandoverCreateReservationFlow = async () => {
    try {
      if (!assetGroupSelection || !orderData) {
        return;
      }

      let pickupFromDate: string;
      let pickupToDate: string;
      let returnFromDate: string;
      let returnToDate: string;

      if (
        selectedServiceType === "Key-Pickup-And-Return" &&
        orderData.checkInDate &&
        orderData.checkOutDate
      ) {
        // Combined flow:
        // Pickup period uses the explicit handover range (checkInDate → checkOutDate)
        pickupFromDate = orderData.checkInDate.toISOString();
        pickupToDate = orderData.checkOutDate.toISOString();

        // Return period uses the dedicated return range if provided; otherwise fall back to a minimal 1-day window after checkout
        if (orderData.keyReturnFromDate && orderData.keyReturnToDate) {
          returnFromDate = orderData.keyReturnFromDate.toISOString();
          returnToDate = orderData.keyReturnToDate.toISOString();
        } else {
          returnFromDate = orderData.checkOutDate.toISOString();
          returnToDate = orderData.checkOutDate.clone().add(1, "days").toISOString();
        }
      } else if (
        selectedServiceType === "Key-Pickup-Only" &&
        orderData.checkInDate
      ) {
        // Pickup only: when a days range is selected via schedule step, checkOutDate is authoritative
        if (orderData.checkOutDate) {
          pickupFromDate = orderData.checkInDate.toISOString();
          pickupToDate = orderData.checkOutDate.toISOString();
        } else {
          // Fallback: derive using quantity days (defaults to 1)
          pickupFromDate = orderData.checkInDate.toISOString();
          pickupToDate = orderData.checkInDate
            .clone()
            .add(orderData.quantity || 1, "days")
            .toISOString();
        }

        // For pickup only, we don't need a separate return entry
        returnFromDate = pickupFromDate;
        returnToDate = pickupToDate;
      } else {
        // Fallback to existing single range logic for other cases
        let fromDate: string;
        let toDate: string;
        if (orderData.checkInDate && orderData.checkOutDate) {
          fromDate = orderData.checkInDate.toISOString();
          toDate = orderData.checkOutDate.toISOString();
        } else {
          // The one single-day window rule (station day + station slot time),
          // not a copy of it — this branch carried its own, on the phone's clock.
          ({ fromDate, toDate } = stationSlotWindow(
            orderData.date!,
            orderData.time,
            orderData.duration,
            timeZone
          ));
        }

        pickupFromDate = fromDate;
        pickupToDate = toDate;
        returnFromDate = fromDate;
        returnToDate = toDate;
      }

      const newReservation: Array<CreateNewReservationData & { customerAgreementId?: string }> = [
        // pick up date
        {
          groupId: assetGroupSelection,
          amount: orderData.amount,
          fromDate: pickupFromDate,
          toDate: pickupToDate,
          isFullDay: false,
          quantity: 1,
          useCase: useCase,
          ...(customerAgreementId ? { customerAgreementId } : {}),
        },
        // return data (only include if it's a pickup and return service)
        ...(selectedServiceType === "Key-Pickup-And-Return"
          ? [
              {
                groupId: assetGroupSelection,
                amount: orderData.amount,
                fromDate: returnFromDate,
                toDate: returnToDate,
                isFullDay: false,
                quantity: 1,
                useCase: useCase,
                ...(customerAgreementId ? { customerAgreementId } : {}),
              },
            ]
          : []),
      ];

      // return console.log("newReservation", newReservation)

      // Call the mutation function
      const response = await createNewReservationMutation(newReservation).unwrap();

      const processedReservation = processReservationData(response);
      if (processedReservation) {
        dispatch(reservationCreated({ reservation: processedReservation }));
      }

      return processedReservation;
    } catch (error) {
      Logger.for("createNewReservation").error(error);
      ErrorTracker.trackException(error as Error);
      // The caller gets `undefined` and stays on the step; without this the
      // customer got no explanation at all (#1970).
      notifyBookingFailure(dispatch, error);
    }
  };

  return keyHandoverCreateReservationFlow;
};